import {useEffect} from 'react';
import {useResizeObserver} from 'usehooks-ts';
import type {CameraOptions, Map as MapboxMap} from 'mapbox-gl';
import getPlaceZoomLevel from 'src/utils/map/getPlaceZoomLevel';
import useWidthBreakpoints from 'src/utils/useWidthBreakpoints';
import getBoundingBox from 'src/utils/map/getBoundingBox';

export const getPlaceCameraPosition = (place: Partial<Place>, zoomOverride?: number) => {
  if (!place?.location?.coordinates) return;

  const zoom = zoomOverride || getPlaceZoomLevel(place);

  const cameraPosition: CameraOptions = {
    center: [place.location.coordinates[0], place.location.coordinates[1]],
    zoom,
    pitch: zoom > 13 ? 50 : 0,
    bearing: 0
  };

  return cameraPosition;
};

export const useMapResize = (mapRef: React.RefObject<HTMLDivElement | null>, map: MapboxMap | null) => {
  // @ts-expect-error
  const {width = 0, height = 0} = useResizeObserver({ref: mapRef});

  useEffect(() => {
    if (!map) return;

    map.resize();
  }, [map, width, height]);
};

type FlyToPlaceOptions = {
  immediate?: boolean;
  force?: boolean;
};

const useMapUtils = (map: MapboxMap | null) => {
  const breakpoint = useWidthBreakpoints([800], 1);

  const isMobile = breakpoint === 0;

  const flyToPlace = (place: Partial<Place>, {immediate}: FlyToPlaceOptions = {}) => {
    if (!map) return;

    const cameraPosition = getPlaceCameraPosition(place);

    if (!cameraPosition) return;

    if (immediate) {
      map.jumpTo(cameraPosition);
    } else {
      map.flyTo({
        ...cameraPosition,
        speed: 1.5,
        maxDuration: 4500,
        offset: isMobile ? [0, 0] : [0, -60]
      });
    }
  };

  const zoomToPlaces = (places: Partial<Place>[]) => {
    if (!map) return;

    if (places.length === 1) return flyToPlace(places[0]);

    const coordinates = places.map((p) => p.location?.coordinates).filter(Boolean);
    const boundingBox = getBoundingBox(coordinates);

    map.fitBounds(boundingBox, {padding: 40});
  };

  return {zoomToPlaces, flyToPlace, isMobile};
};

export default useMapUtils;
